import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  LinearProgress,
  Checkbox,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  CircularProgress,
  Alert,
  Tooltip,
  Chip,
  Card,
  CardContent,
  Divider,
  Pagination
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { vi } from 'date-fns/locale';
import {
  Refresh as RefreshIcon,
  Delete as DeleteIcon,
  Download as DownloadIcon,
  Restore as RestoreIcon,
  Storage as StorageIcon,
  Add as AddIcon,
  Schedule as ScheduleIcon,
  CloudUpload as CloudUploadIcon
} from '@mui/icons-material';
import { backupService, type Backup } from '../services/backupService';
import { useSnackbar } from '../hooks/useSnackbar';
import { ToastNotification } from '../components/ToastNotification';
import { logger } from '../utils/logger';

export const BackupManagementPage: React.FC = () => {
  const [backups, setBackups] = useState<Backup[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [restoreDialogOpen, setRestoreDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedBackup, setSelectedBackup] = useState<Backup | null>(null);
  const [restoring, setRestoring] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [manualBackingUp, setManualBackingUp] = useState(false);
  const [scheduleLoading, setScheduleLoading] = useState(false);
  const [scheduleSaving, setScheduleSaving] = useState(false);
  const [scheduleValue, setScheduleValue] = useState(''); // keep raw (for fallback)
  const [dailyTime, setDailyTime] = useState<Date | null>(new Date());
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [restoreUploadedDialogOpen, setRestoreUploadedDialogOpen] = useState(false);
  const { snackbar, showSuccess, showError, hideSnackbar } = useSnackbar();

  useEffect(() => {
    loadSchedule();
  }, []);

  useEffect(() => {
    loadBackups();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize, searchTerm]);

  const loadBackups = async () => {
    try {
      setLoading(true);
      const response = await backupService.getBackups({ page, pageSize, search: searchTerm });
      setBackups(response.backups);
      setTotalCount(response.totalCount || 0);
      setTotalPages(Math.max(1, response.totalPages || Math.ceil((response.totalCount || 0) / pageSize) || 1));
    } catch (error: any) {
      logger.error('Error loading backups:', error);
      showError('Không thể tải danh sách backup');
    } finally {
      setLoading(false);
    }
  };

  const loadSchedule = async () => {
    try {
      setScheduleLoading(true);
      const res = await backupService.getSchedule();
      const schedule = (res.schedule || '').trim();
      setScheduleValue(schedule);

      // Parse hour/minute from cron: "m h" or "m h * * *"
      const parts = schedule.split(/\s+/);
      if (parts.length >= 2) {
        const minute = Number(parts[0]);
        const hour = Number(parts[1]);
        if (!Number.isNaN(minute) && !Number.isNaN(hour)) {
          const d = new Date();
          d.setHours(hour, minute, 0, 0);
          setDailyTime(d);
        }
      }
    } catch (error: any) {
      logger.error('Error loading backup schedule:', error);
      showError('Không thể tải lịch backup');
    } finally {
      setScheduleLoading(false);
    }
  };

  const handleSaveSchedule = async () => {
    try {
      setScheduleSaving(true);
      // Friendly UI: save as daily cron "minute hour"
      if (!dailyTime) {
        showError('Vui lòng chọn giờ backup');
        return;
      }
      const minute = dailyTime.getMinutes();
      const hour = dailyTime.getHours();
      const cron = `${minute} ${hour}`;

      const res = await backupService.updateSchedule(cron);
      showSuccess(res.message || 'Cập nhật lịch backup thành công!');
      setScheduleValue(res.schedule || cron);
    } catch (error: any) {
      logger.error('Error saving backup schedule:', error);
      showError(error?.message || error?.response?.data?.error || 'Không thể cập nhật lịch backup');
    } finally {
      setScheduleSaving(false);
    }
  };

  const handleManualBackup = async () => {
    try {
      setManualBackingUp(true);
      const res = await backupService.createBackupNow();
      showSuccess(res.message || 'Backup thủ công thành công!');
      await loadBackups();
    } catch (error: any) {
      logger.error('Error creating manual backup:', error);
      showError(error?.message || error?.response?.data?.error || 'Backup thủ công thất bại');
    } finally {
      setManualBackingUp(false);
    }
  };

  const handleRestoreClick = (backup: Backup) => {
    setSelectedBackup(backup);
    setRestoreDialogOpen(true);
  };

  const handleRestoreConfirm = async () => {
    if (!selectedBackup) return;

    try {
      setRestoring(true);
      await backupService.restoreBackup(selectedBackup.filename);
      showSuccess('Khôi phục database thành công!');
      setRestoreDialogOpen(false);
      setSelectedBackup(null);
      // Reload backups after restore
      setTimeout(() => {
        loadBackups();
      }, 1000);
    } catch (error: any) {
      logger.error('Error restoring backup:', error);
      const apiError = error?.response?.data?.error;
      const apiDetails = error?.response?.data?.details;
      showError(apiDetails ? `${apiError || 'Không thể khôi phục database'}\n\n${apiDetails}` : (apiError || 'Không thể khôi phục database'));
    } finally {
      setRestoring(false);
    }
  };

  const handleDeleteClick = (backup: Backup) => {
    setSelectedBackup(backup);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!selectedBackup) return;

    try {
      setDeleting(true);
      await backupService.deleteBackup(selectedBackup.filename);
      showSuccess('Xóa backup thành công!');
      setDeleteDialogOpen(false);
      setSelectedBackup(null);
      loadBackups();
    } catch (error: any) {
      logger.error('Error deleting backup:', error);
      showError(error?.response?.data?.error || 'Không thể xóa backup');
    } finally {
      setDeleting(false);
    }
  };

  const handleDownload = async (backup: Backup) => {
    try {
      const blob = await backupService.downloadBackup(backup.filename);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = backup.filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      showSuccess('Tải backup thành công!');
    } catch (error: any) {
      logger.error('Error downloading backup:', error);
      showError(error?.response?.data?.error || 'Không thể tải backup');
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('vi-VN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <StorageIcon sx={{ fontSize: 32, color: 'primary.main' }} />
          <Typography variant="h4" component="h1">
            Quản Lý Backup Database
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title="Backup thủ công" arrow>
            <span>
              <IconButton
                color="primary"
                onClick={handleManualBackup}
                aria-label="Backup thủ công"
                size="small"
                disabled={manualBackingUp || loading}
              >
                <AddIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Upload backup" arrow>
            <span>
              <IconButton
                color="primary"
                onClick={() => setUploadDialogOpen(true)}
                aria-label="Upload backup"
                size="small"
                disabled={loading}
              >
                <CloudUploadIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Làm mới" arrow>
            <span>
              <IconButton
                color="primary"
                onClick={() => {
                  loadSchedule();
                  loadBackups();
                }}
                aria-label="Làm mới"
                size="small"
                disabled={loading || scheduleLoading}
              >
                <RefreshIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      </Box>

      {/* Compact filter/search + schedule row */}
      <Paper sx={{ p: 1.5, mb: 2 }}>
        <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={vi}>
          <Box sx={{ display: 'flex', gap: 1.25, alignItems: 'center', flexWrap: 'wrap' }}>
            <TextField
              size="small"
              label="Tìm kiếm"
              placeholder="Nhập tên file backup..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setPage(1);
              }}
              sx={{ flex: 1, minWidth: 260 }}
            />
            <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
              Tổng: {totalCount}
            </Typography>
            <Divider orientation="vertical" flexItem sx={{ mx: 0.5, display: { xs: 'none', md: 'block' } }} />
            <TimePicker
              label="Giờ backup"
              value={dailyTime}
              onChange={(v) => setDailyTime(v)}
              disabled={scheduleLoading || scheduleSaving}
              slotProps={{ textField: { size: 'small', sx: { minWidth: 170 } } as any }}
            />
            <Tooltip title="Cập nhật lịch" arrow>
              <span>
                <IconButton
                  color="primary"
                  onClick={handleSaveSchedule}
                  aria-label="Cập nhật lịch"
                  size="small"
                  disabled={scheduleSaving || scheduleLoading || !dailyTime}
                >
                  {scheduleSaving ? <CircularProgress size={18} /> : <ScheduleIcon fontSize="small" />}
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        </LocalizationProvider>
        {!!scheduleValue && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
            Cron: {scheduleValue}
          </Typography>
        )}
      </Paper>

      {manualBackingUp && (
        <Box sx={{ mb: 2 }}>
          <LinearProgress />
          <Typography variant="caption" color="text.secondary">
            Đang tạo backup thủ công, vui lòng chờ...
          </Typography>
        </Box>
      )}

      <Alert severity="info" sx={{ mb: 3 }}>
        <Typography variant="body2">
          <strong>Lưu ý:</strong> Bạn có thể tạo backup thủ công hoặc dùng lịch tự động.
          <br />
          <strong>Cảnh báo:</strong> Restore sẽ ghi đè dữ liệu hiện tại. Hãy đảm bảo bạn đã backup dữ liệu hiện tại trước khi khôi phục.
        </Typography>
      </Alert>

      {loading && backups.length === 0 ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : backups.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="body1" color="text.secondary">
            Chưa có backup nào
          </Typography>
        </Paper>
      ) : (
      <>
      <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: '5%' }} />
                <TableCell sx={{ width: '30%' }}><strong>Tên file</strong></TableCell>
                <TableCell sx={{ width: '15%' }}><strong>Kích thước</strong></TableCell>
                <TableCell sx={{ width: '15%' }}><strong>Ngày tạo</strong></TableCell>
                <TableCell sx={{ width: '15%' }}><strong>Ngày sửa</strong></TableCell>
                <TableCell align="center" sx={{ width: '20%' }}><strong>Thao tác</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {backups.map((backup) => (
                <TableRow key={backup.filename} hover selected={selectedBackup?.filename === backup.filename}>
                  <TableCell>
                    <Checkbox
                      checked={selectedBackup?.filename === backup.filename}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedBackup(backup);
                        } else {
                          setSelectedBackup(null);
                        }
                      }}
                      inputProps={{ 'aria-label': `Chọn backup ${backup.filename}` }}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight="medium">
                      {backup.filename}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={backupService.formatFileSize(backup.size)}
                      size="small"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>{formatDate(backup.created)}</TableCell>
                  <TableCell>{formatDate(backup.modified)}</TableCell>
                  <TableCell align="center">
                    <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                      <Tooltip title="Khôi phục (cần chọn file)">
                        <span>
                          <IconButton
                            size="small"
                            color="primary"
                            disabled={!selectedBackup || selectedBackup.filename !== backup.filename}
                            onClick={() => handleRestoreClick(backup)}
                          >
                            <RestoreIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title="Tải xuống">
                        <IconButton
                          size="small"
                          color="secondary"
                          onClick={() => handleDownload(backup)}
                        >
                          <DownloadIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Xóa">
                        <span>
                          <IconButton
                            size="small"
                            color="error"
                            disabled={!selectedBackup || selectedBackup.filename !== backup.filename}
                            onClick={() => handleDeleteClick(backup)}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

      {totalPages > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
          <Pagination
            count={totalPages}
            page={page}
            onChange={(_, newPage) => setPage(newPage)}
            color="primary"
          />
        </Box>
      )}
      </>
      )}

      {/* Restore Confirmation Dialog */}
      <Dialog open={restoreDialogOpen} onClose={() => !restoring && setRestoreDialogOpen(false)}>
        <DialogTitle>Xác nhận khôi phục</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Bạn có chắc chắn muốn khôi phục database từ backup <strong>{selectedBackup?.filename}</strong>?
            <br />
            <br />
            <strong style={{ color: 'red' }}>Cảnh báo:</strong> Thao tác này sẽ ghi đè toàn bộ dữ liệu hiện tại trong database.
            Dữ liệu hiện tại sẽ bị mất và không thể khôi phục lại.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRestoreDialogOpen(false)} disabled={restoring}>
            Hủy
          </Button>
          <Button
            onClick={handleRestoreConfirm}
            color="error"
            variant="contained"
            disabled={restoring}
            startIcon={restoring ? <CircularProgress size={20} /> : <RestoreIcon />}
          >
            {restoring ? 'Đang khôi phục...' : 'Khôi phục'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => !deleting && setDeleteDialogOpen(false)}>
        <DialogTitle>Xác nhận xóa</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Bạn có chắc chắn muốn xóa backup <strong>{selectedBackup?.filename}</strong>?
            <br />
            Thao tác này không thể hoàn tác.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={deleting}>
            Hủy
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            disabled={deleting}
            startIcon={deleting ? <CircularProgress size={20} /> : <DeleteIcon />}
          >
            {deleting ? 'Đang xóa...' : 'Xóa'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Upload Backup Dialog */}
      <Dialog open={uploadDialogOpen} onClose={() => !uploading && setUploadDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Upload file backup</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Chọn file backup (.backup) từ máy tính của bạn để upload và khôi phục.
          </DialogContentText>
          <input
            accept=".backup"
            style={{ display: 'none' }}
            id="backup-upload-input"
            type="file"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) {
                if (!file.name.endsWith('.backup')) {
                  showError('Chỉ chấp nhận file .backup');
                  return;
                }
                setUploadedFile(file);
              }
            }}
          />
          <label htmlFor="backup-upload-input">
            <Button variant="outlined" component="span" fullWidth startIcon={<CloudUploadIcon />}>
              Chọn file backup
            </Button>
          </label>
          {uploadedFile && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2">
                <strong>File đã chọn:</strong> {uploadedFile.name}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Kích thước: {backupService.formatFileSize(uploadedFile.size)}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setUploadDialogOpen(false);
            setUploadedFile(null);
          }} disabled={uploading}>
            Hủy
          </Button>
          <Button
            onClick={async () => {
              if (!uploadedFile) {
                showError('Vui lòng chọn file backup');
                return;
              }
              try {
                setUploading(true);
                await backupService.uploadBackup(uploadedFile);
                showSuccess('Upload backup thành công!');
                setUploadDialogOpen(false);
                await loadBackups();
                // Auto-open restore dialog
                setRestoreUploadedDialogOpen(true);
              } catch (error: any) {
                showError(error?.message || 'Upload backup thất bại');
              } finally {
                setUploading(false);
              }
            }}
            variant="contained"
            color="primary"
            disabled={!uploadedFile || uploading}
            startIcon={uploading ? <CircularProgress size={20} /> : <CloudUploadIcon />}
          >
            {uploading ? 'Đang upload...' : 'Upload'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Restore Uploaded Backup Dialog */}
      <Dialog open={restoreUploadedDialogOpen} onClose={() => !restoring && setRestoreUploadedDialogOpen(false)}>
        <DialogTitle>Khôi phục từ file đã upload</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Bạn có muốn khôi phục database từ file backup vừa upload không?
            <br />
            <br />
            <strong style={{ color: 'red' }}>Cảnh báo:</strong> Thao tác này sẽ ghi đè toàn bộ dữ liệu hiện tại trong database.
            Dữ liệu hiện tại sẽ bị mất và không thể khôi phục lại.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRestoreUploadedDialogOpen(false)} disabled={restoring}>
            Hủy
          </Button>
          <Button
            onClick={async () => {
              if (!uploadedFile) return;
              try {
                setRestoring(true);
                await backupService.restoreBackup(uploadedFile.name);
                showSuccess('Khôi phục database thành công!');
                setRestoreUploadedDialogOpen(false);
                setUploadedFile(null);
                setTimeout(() => {
                  loadBackups();
                }, 1000);
              } catch (error: any) {
                logger.error('Error restoring uploaded backup:', error);
                const apiError = error?.response?.data?.error;
                const apiDetails = error?.response?.data?.details;
                showError(apiDetails ? `${apiError || 'Không thể khôi phục database'}\n\n${apiDetails}` : (apiError || 'Không thể khôi phục database'));
              } finally {
                setRestoring(false);
              }
            }}
            color="error"
            variant="contained"
            disabled={restoring}
            startIcon={restoring ? <CircularProgress size={20} /> : <RestoreIcon />}
          >
            {restoring ? 'Đang khôi phục...' : 'Khôi phục'}
          </Button>
        </DialogActions>
      </Dialog>

      <ToastNotification
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={hideSnackbar}
      />
    </Box>
  );
};

